import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Extent } from './extent';

dayjs.extend(utc);
dayjs.extend(timezone);

/** One hour, the step handles snap to and move by. */
export const HOUR_MS = 60 * 60 * 1000;

/** The narrowest window the handles can select (PRD §4.1). */
export const MIN_SELECTION_MS = 6 * HOUR_MS;

/** What a drag or key press moves: one handle, or the band between them. */
export enum SelectionTarget {
  Start = 'start',
  End = 'end',
  Band = 'band',
}

/** A fixed marker at pickup or delivery in the full range. */
export interface Origin {
  at: number;
  label: string;
}

/** The instant rounded to the nearest whole hour on the timezone's wall clock. */
export const snapToHour = (ms: number, timeZone: string): number => {
  const floor = dayjs(ms).tz(timeZone).startOf('hour');
  return (
    ms - floor.valueOf() >= HOUR_MS / 2 ? floor.add(1, 'hour') : floor
  ).valueOf();
};

/**
 * The span the handles can reach: the window, cut short at the first not-loaded span on each side of the
 * operational window, since nothing was downloaded there to select.
 */
export const selectableExtent = (
  window: Extent,
  operational: Extent,
  blocked: readonly Extent[]
): Extent => {
  let lo = window[0];
  let hi = window[1];
  for (const [from, to] of blocked) {
    if (to <= operational[0]) lo = Math.max(lo, to);
    if (from >= operational[1]) hi = Math.min(hi, from);
  }
  return [lo, hi];
};

/**
 * The selection cut to what can be selected, for when a tail fails to load after a handle was dragged into it;
 * `fallback` when too little is left.
 */
export const fitSelection = (
  selection: Extent,
  selectable: Extent,
  fallback: Extent
): Extent => {
  if (selection[0] >= selectable[0] && selection[1] <= selectable[1]) {
    return selection;
  }
  const start = Math.max(selection[0], selectable[0]);
  const end = Math.min(selection[1], selectable[1]);
  return end - start >= MIN_SELECTION_MS ? [start, end] : fallback;
};

/** True when the operational window is long enough to place handles on. */
export const canSelect = (operational: Extent): boolean =>
  operational[1] - operational[0] >= MIN_SELECTION_MS;

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(Math.max(value, lo), hi);

/**
 * The selection after moving `target` by `deltaMs` from `from`: snapped to the hour, kept inside
 * `selectable`, and never narrower than the minimum window. Moving the band keeps its width.
 */
export const moveSelection = (
  from: Extent,
  target: SelectionTarget,
  deltaMs: number,
  selectable: Extent,
  timeZone: string
): Extent => {
  const [start, end] = from;
  switch (target) {
    case SelectionTarget.Start:
      return [
        clamp(
          snapToHour(start + deltaMs, timeZone),
          selectable[0],
          end - MIN_SELECTION_MS
        ),
        end,
      ];
    case SelectionTarget.End:
      return [
        start,
        clamp(
          snapToHour(end + deltaMs, timeZone),
          start + MIN_SELECTION_MS,
          selectable[1]
        ),
      ];
    case SelectionTarget.Band: {
      const width = end - start;
      const moved = clamp(
        snapToHour(start + deltaMs, timeZone),
        selectable[0],
        selectable[1] - width
      );
      return [moved, moved + width];
    }
    default: {
      const unhandled: never = target;
      return unhandled;
    }
  }
};

/** The selection's live readout, e.g. "Jan 2, 5:00 AM to Jan 8, 3:00 AM · 5d 22h". */
export const selectionLabel = (selection: Extent, timeZone: string): string => {
  const format = (ms: number) => dayjs(ms).tz(timeZone).format('MMM D, h:mm A');
  const hours = Math.floor((selection[1] - selection[0]) / HOUR_MS);
  return `${format(selection[0])} to ${format(selection[1])} · ${Math.floor(hours / 24)}d ${hours % 24}h`;
};

/** The origin lines to draw: pickup and delivery when known, except where a handle sits exactly on one. */
export const visibleOrigins = (
  pickedUp: number | null,
  delivered: number | null,
  selection: Extent
): Origin[] =>
  [
    pickedUp === null ? null : { at: pickedUp, label: 'Picked up' },
    delivered === null ? null : { at: delivered, label: 'Delivered' },
  ].filter(
    (origin): origin is Origin =>
      origin !== null &&
      origin.at !== selection[0] &&
      origin.at !== selection[1]
  );
